import { IRcsConfig, IRcsActionConfig, defaultRcsConfig } from '../context/useRcsTemplateEditorContext';
import { RcsCardContent, RcsCardButton, RcsTemplatePayload } from '../types/rcsTemplateData';

function hasStoredSmsFallback(editor: unknown): boolean {
  if (!editor || typeof editor !== 'object') return false;
  const editorRecord = editor as Record<string, unknown>;
  const id = typeof editorRecord.smsFallbackTemplateId === 'string' ? editorRecord.smsFallbackTemplateId.trim() : '';
  const content = typeof editorRecord.smsFallbackContent === 'string' ? editorRecord.smsFallbackContent.trim() : '';
  const name =
    typeof editorRecord.smsFallbackTemplateName === 'string' ? editorRecord.smsFallbackTemplateName.trim() : '';
  return id.length > 0 || content.length > 0 || name.length > 0;
}

/** Extracts card from compiled RCS JSON; returns null if invalid or missing card. */
export function getCardFromCompiledRcs(compiledJson: string): RcsCardContent | null {
  if (!compiledJson.trim()) return null;
  try {
    const parsed = JSON.parse(compiledJson) as RcsTemplatePayload;
    const card = parsed?.messages?.[0]?.content?.card;
    return card ?? null;
  } catch {
    return null;
  }
}

/**
 * Maps saved template body (JSON string) to IRcsConfig for editor state.
 * Returns defaultRcsConfig only for empty/whitespace body. Returns null for malformed JSON
 * or non-empty body that lacks a card, so the caller can show load-error UI.
 */
export function templateBodyToRcsConfig(templateBody: string | undefined): IRcsConfig | null {
  const trimmed = templateBody?.trim() ?? '';
  if (trimmed.length === 0) {
    return defaultRcsConfig;
  }

  let parsed: RcsTemplatePayload & { _rcsEditor?: Record<string, unknown> };
  try {
    parsed = JSON.parse(templateBody!) as RcsTemplatePayload & { _rcsEditor?: Record<string, unknown> };
  } catch {
    return null;
  }

  const card = parsed?.messages?.[0]?.content?.card ?? null;
  const editorSettings = parsed?._rcsEditor;

  if (!card) return null;

  const actions: IRcsActionConfig[] = (card.buttons ?? []).map((btn, index) => {
    const id = index === 0 ? 'default-1' : `action-${index}`;
    const actionType = btn.openUrl ? 'openUrl' : btn.dial ? 'dialPhone' : 'openUrl';
    const actionUrl = btn.openUrl?.url ?? btn.dial?.phoneNumber ?? '';
    return {
      id,
      actionType,
      buttonLabel: btn.caption ?? '',
      actionUrl,
    };
  });

  if (actions.length === 0) {
    actions.push({
      id: 'default-1',
      actionType: 'openUrl',
      buttonLabel: '',
      actionUrl: '',
    });
  }

  return {
    ...defaultRcsConfig,
    cardTitle: card.title ?? '',
    description: card.text ?? '',
    imageUrl: card.media?.source ?? '',
    actions,
    smsFallbackEnabled: hasStoredSmsFallback(editorSettings),
    smsFallbackTemplateId:
      typeof editorSettings?.smsFallbackTemplateId === 'string'
        ? editorSettings.smsFallbackTemplateId
        : defaultRcsConfig.smsFallbackTemplateId,
    smsFallbackTemplateName:
      typeof editorSettings?.smsFallbackTemplateName === 'string'
        ? editorSettings.smsFallbackTemplateName
        : defaultRcsConfig.smsFallbackTemplateName,
    smsFallbackContent:
      typeof editorSettings?.smsFallbackContent === 'string'
        ? editorSettings.smsFallbackContent
        : defaultRcsConfig.smsFallbackContent,
  };
}

/** Builds full RCS template JSON for the compiler (Link Mobility format); includes _rcsEditor for round-trip of editor settings. */
export function rcsConfigToCompilerTemplateValue(config: IRcsConfig): string {
  const card = rcsConfigToCardContent(config);
  const persistFallback = config.smsFallbackEnabled === true;
  const payload: RcsTemplatePayload & { _rcsEditor: Record<string, unknown> } = {
    messages: [
      {
        channelId: 'rcs',
        userId: '{{phone.mobile}}',
        content: { card },
      },
    ],
    _rcsEditor: persistFallback
      ? {
          smsFallbackTemplateId: config.smsFallbackTemplateId ?? '',
          smsFallbackTemplateName: config.smsFallbackTemplateName ?? '',
          smsFallbackContent: config.smsFallbackContent ?? '',
        }
      : {
          smsFallbackTemplateId: '',
          smsFallbackTemplateName: '',
          smsFallbackContent: '',
        },
  };
  return JSON.stringify(payload);
}

/** Maps IRcsConfig to Link Mobility RcsCardContent for the RichCard preview. */
export function rcsConfigToCardContent(config: IRcsConfig): RcsCardContent {
  const title = config.cardTitle ?? '';
  const text = config.description ?? '';
  const trimmedImageUrl = config.imageUrl?.trim();
  const media = trimmedImageUrl ? { source: trimmedImageUrl, type: 'image/jpeg' } : undefined;

  const actionsWithLabel = config.actions.filter(action => (action.buttonLabel ?? '').trim().length > 0);
  const buttons: RcsCardButton[] = actionsWithLabel.map(action => {
    const caption = (action.buttonLabel ?? '').trim();
    const postback = action.actionType || action.id;
    const base: RcsCardButton = { caption, postback };
    const trimmedActionUrl = action.actionUrl?.trim();

    if (action.actionType === 'dialPhone' && trimmedActionUrl) {
      return { ...base, dial: { phoneNumber: trimmedActionUrl } };
    }
    if (action.actionType === 'openUrl' && trimmedActionUrl) {
      return { ...base, openUrl: { url: trimmedActionUrl } };
    }
    return base;
  });

  return { title, text, media, buttons };
}
